/**
 * Mes événements — liste réelle, métriques par événement, import CSV assisté
 * (détection de colonnes), lien webhook Zapier, registre des consentements.
 * Reprend les fonctions de l'ancien tableau organisateur dans le design Lovable.
 */
import { useEffect, useState } from 'react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, PageTitle, SectionLabel, Table, Toggle } from '../ui.tsx';
import { libelleCategorie } from '../../ecrans/compte/types.ts';
import { ProgrammeEditeur } from '../../ecrans/compte/ProgrammeEditeur.tsx';
import type { EvenementAdmin } from '../EspaceAdmin.tsx';

type Apercu = {
  separateur: string;
  entetes: string[];
  correspondance: Record<string, number | undefined>;
  problemes: string[];
  importables: number;
  rejets: { ligne: number; raison: string }[];
};

type ResultatImport = {
  inscrits: { email: string; prenom: string | null; nom: string | null; lien: string }[];
  dejaInscrits: number;
  rejets: { ligne: number; raison: string }[];
};

const dateCourte = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', year: 'numeric' });

export function Evenements({ evenements }: { evenements: EvenementAdmin[] }) {
  const [selection, setSelection] = useState<EvenementAdmin | null>(evenements[0] ?? null);

  return (
    <>
      <PageTitle
        title="Mes événements"
        subtitle="Participants, imports billetterie et registre des consentements, événement par événement."
      />

      <Table head={['Événement', 'Catégorie', 'Date', 'Lieu', 'Statut', '']}>
        {evenements.map((e) => (
          <tr key={e.id} className="cursor-pointer" onClick={() => setSelection(e)}>
            <td className="text-blanc">{e.nom}</td>
            <td className="text-fumee">{libelleCategorie(e.categorie, e.sous_categorie)}</td>
            <td className="text-fumee">{dateCourte(e.debut_le)}</td>
            <td className="text-fumee">{[e.lieu_nom, e.lieu_ville].filter(Boolean).join(', ')}</td>
            <td>
              <Badge filled={e.statut === 'ouvert' || e.statut === 'en_cours'}>{e.statut}</Badge>
            </td>
            <td className="text-fumee">{selection?.id === e.id ? '●' : ''}</td>
          </tr>
        ))}
        {evenements.length === 0 && (
          <tr>
            <td colSpan={6} className="text-fumee">
              Aucun événement pour votre organisation.
            </td>
          </tr>
        )}
      </Table>

      {selection && <DetailEvenement evenement={selection} />}
    </>
  );
}

function DetailEvenement({ evenement }: { evenement: EvenementAdmin }) {
  const [webhook, setWebhook] = useState<string | null>(null);

  useEffect(() => {
    setWebhook(null);
    void appelerApi<{ webhook: string }>(`/api/import/historique/${evenement.id}`).then((r) => {
      if (r.donnees) setWebhook(r.donnees.webhook);
    });
  }, [evenement.id]);

  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      <Card className="lg:col-span-2">
        <SectionLabel>Programme de la journée</SectionLabel>
        <p className="mt-2 text-[14px] leading-relaxed text-fumee">
          Accueil, conférences par salle avec leurs intervenants, speed meetings, tables rondes, keynote, afterwork.
          Les participants y déclarent « j’y assiste » et voient qui d’autre y sera ; le public le lit sur la fiche.
        </p>
        <div className="mt-4">
          <ProgrammeEditeur base={`/api/sessions/organisateur/${evenement.id}`} jourParDefaut={evenement.debut_le} />
        </div>
      </Card>
      <ImportCsv evenementId={evenement.id} />

      <div className="flex flex-col gap-4">
        <InscriptionLibre evenement={evenement} />
        <Card>
          <SectionLabel>Webhook Zapier / Make</SectionLabel>
          <p className="mt-2 text-[14px] leading-relaxed text-fumee">
            Chaque billet vendu sur votre billetterie peut inscrire le participant automatiquement :
            envoyez un POST sur cette URL.
          </p>
          {webhook && (
            <div className="mt-3 flex items-center gap-2">
              <code className="block flex-1 truncate rounded-[8px] border border-graphite px-3 py-2 font-mono text-[12px] text-fumee">
                {webhook}
              </code>
              <Btn onClick={() => void navigator.clipboard.writeText(webhook)}>Copier</Btn>
            </div>
          )}
        </Card>

        <Card>
          <SectionLabel>Registre des consentements</SectionLabel>
          <p className="mt-2 text-[14px] leading-relaxed text-fumee">
            L'historique horodaté de chaque accord et retrait, acte par acte — votre preuve RGPD.
          </p>
          <a
            className="mt-4 inline-block rounded-full border border-graphite px-4 py-2 text-[14px] text-fumee transition-colors hover:text-blanc"
            href={`/api/organisateur/evenements/${evenement.id}/registre-consentements.csv`}
          >
            Télécharger le registre (CSV)
          </a>
        </Card>
      </div>
    </div>
  );
}

/**
 * Inscription libre — l'événement apparaît (ou non) dans la liste « à
 * rejoindre » des comptes personnels. Opt-in : un séminaire interne ne doit
 * pas s'ouvrir au public parce qu'il est passé au statut « ouvert ».
 */
function InscriptionLibre({ evenement }: { evenement: EvenementAdmin }) {
  const [visible, setVisible] = useState(Boolean(evenement.visibilite_publique));
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setVisible(Boolean(evenement.visibilite_publique));
    setMessage(null);
  }, [evenement.id, evenement.visibilite_publique]);

  const basculer = async () => {
    const suivant = !visible;
    setVisible(suivant);
    const r = await envoyer(
      `/api/organisateur/evenements/${evenement.id}/visibilite-publique`,
      { visible: suivant },
      'PUT',
    );
    if (r.erreur) {
      setVisible(!suivant);
      setMessage(r.erreur);
      return;
    }
    setMessage(
      suivant
        ? 'Visible : n’importe qui peut le rejoindre depuis son compte EventMatch.'
        : 'Masqué : on n’y entre que par code ou lien personnel.',
    );
  };

  return (
    <Card>
      <SectionLabel>Inscription libre</SectionLabel>
      <p className="mt-2 text-[14px] leading-relaxed text-fumee">
        Afficher cet événement dans la liste publique « à rejoindre » : les entrepreneurs ayant un
        compte EventMatch s’inscrivent en un clic, profil déjà rempli.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button onClick={() => void basculer()} className="flex items-center gap-3">
          <Toggle on={visible} />
          <span className="text-[14px] text-blanc">{visible ? 'Ouvert à tous' : 'Sur invitation'}</span>
        </button>
      </div>
      {message && <p className="mt-3 text-[13px] text-fumee">{message}</p>}
    </Card>
  );
}

function ImportCsv({ evenementId }: { evenementId: string }) {
  const [contenu, setContenu] = useState<string | null>(null);
  const [nomFichier, setNomFichier] = useState('');
  const [apercu, setApercu] = useState<Apercu | null>(null);
  const [resultat, setResultat] = useState<ResultatImport | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    setContenu(null);
    setApercu(null);
    setResultat(null);
    setErreur(null);
  }, [evenementId]);

  const lireFichier = (fichier: File) => {
    setNomFichier(fichier.name);
    setResultat(null);
    setApercu(null);
    const lecteur = new FileReader();
    lecteur.onload = async () => {
      const texte = String(lecteur.result ?? '');
      setContenu(texte);
      const r = await envoyer<{ apercu: Apercu }>(`/api/import/csv/${evenementId}?simuler=1`, {
        contenu: texte,
      });
      if (r.erreur) setErreur(r.erreur);
      else setApercu(r.donnees!.apercu);
    };
    lecteur.readAsText(fichier);
  };

  const importer = async () => {
    if (!contenu) return;
    setEnCours(true);
    setErreur(null);
    const r = await envoyer<{ resultat: ResultatImport }>(`/api/import/csv/${evenementId}`, { contenu });
    setEnCours(false);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setResultat(r.donnees!.resultat);
    setApercu(null);
  };

  const telechargerLiens = () => {
    if (!resultat) return;
    const csv =
      '﻿email;prenom;nom;lien_acces\r\n' +
      resultat.inscrits.map((i) => [i.email, i.prenom ?? '', i.nom ?? '', i.lien].join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'liens-acces-participants.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Card>
      <SectionLabel>Importer des participants (CSV)</SectionLabel>
      <p className="mt-2 text-[14px] leading-relaxed text-fumee">
        Exportez la liste depuis votre billetterie et déposez le fichier — les colonnes (nom,
        entreprise, e-mail…) sont détectées automatiquement, en-têtes FR/EN/NL compris.
      </p>

      <input
        type="file"
        accept=".csv,text/csv,text/plain"
        className="mt-4 block text-[13px] text-fumee file:mr-3 file:cursor-pointer file:rounded-full file:border file:border-graphite file:bg-transparent file:px-4 file:py-2 file:text-[13px] file:text-fumee hover:file:text-blanc"
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          if (fichier) lireFichier(fichier);
        }}
      />

      {apercu && (
        <div className="mt-4 flex flex-col gap-3 border-t border-graphite pt-4">
          <p className="text-[14px] text-blanc">
            {nomFichier} — {apercu.importables} ligne(s) importable(s), {apercu.rejets.length} rejet(s)
          </p>
          <p className="text-[13px] text-fumee">
            Colonnes reconnues :{' '}
            {Object.entries(apercu.correspondance)
              .filter(([, v]) => v !== undefined)
              .map(([cible, indice]) => `${cible} → « ${apercu.entetes[indice!]} »`)
              .join(', ') || 'aucune'}
          </p>
          {apercu.problemes.map((p) => (
            <p key={p} className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>
              {p}
            </p>
          ))}
          {apercu.rejets.slice(0, 5).map((r) => (
            <p key={r.ligne} className="text-[13px] text-fumee">
              ligne {r.ligne} : {r.raison}
            </p>
          ))}
          <Btn variant="primary" disabled={enCours || apercu.importables === 0} onClick={() => void importer()}>
            {enCours ? 'Import…' : `Importer ${apercu.importables} participant(s)`}
          </Btn>
        </div>
      )}

      {resultat && (
        <div className="mt-4 flex flex-col gap-3 border-t border-graphite pt-4">
          <p className="text-[14px] text-blanc">
            {resultat.inscrits.length} participant(s) inscrit(s)
            {resultat.dejaInscrits > 0 ? `, ${resultat.dejaInscrits} déjà présent(s)` : ''}
            {resultat.rejets.length > 0 ? `, ${resultat.rejets.length} rejet(s)` : ''}.
          </p>
          {resultat.inscrits.length > 0 && (
            <>
              <p className="text-[13px] text-fumee">
                Téléchargez les liens d'accès maintenant : pour des raisons de sécurité, ils ne
                pourront plus être réaffichés.
              </p>
              <Btn variant="primary" onClick={telechargerLiens}>
                Télécharger les liens d'accès (CSV)
              </Btn>
            </>
          )}
        </div>
      )}

      {erreur && (
        <p className="mt-3 text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
          {erreur}
        </p>
      )}
    </Card>
  );
}
